import { useState } from "react";
import type { ReactNode } from "react";
import {
  MdAnalytics,
  MdPendingActions,
  MdCalendarMonth,
  MdBuild,
  MdSms,
  MdMenuBook,
  MdSettings,
  MdReceiptLong,
  MdCloudUpload,
  MdAssignmentTurnedIn,
} from "react-icons/md";
import AdminBookings from "./all-bookings";
import PendingEICRApprovals from "./pending-eicr-approvals";
import SMSLogs from "./sms-logs";
import Admin from "./admin-portal";
import AdminDocs from "./docs";
import MyConstructorEICR from "./myconstructor-eicr";
import GoogleAnalyticsDashboard from "./google-analytics";
import InvoiceGenerator from "./invoice-generator";
import LegacyCertificatesUpload from "./legacy-certificates-upload";
// import QuoteGenerator from "./quote-generator";
import EICRForm from "./EICRForm";
import SignOutButton from "../../sign-out";

type AdminTab = "bookings" | "reports" | "pending-eicr" | "sms-logs" | "analytics" | "settings" | "docs" | "myconstructor" | "invoice-generator" | "legacy-certificates" | "eicr-admin";
// | "quote-generator";

export default function AdminLayout({ user, role }: any) {
  const [activeTab, setActiveTab] = useState<AdminTab>("pending-eicr");

  const tabs: Array<{ id: AdminTab; label: string; icon: ReactNode }> = [
    { id: "pending-eicr", label: "Pending EICR", icon: <MdPendingActions /> },
    { id: "bookings", label: "Bookings", icon: <MdCalendarMonth /> },
    { id: "eicr-admin", label: "EICR Upload", icon: <MdAssignmentTurnedIn /> },
    { id: "myconstructor", label: "MyConstructor Bookings", icon: <MdBuild /> },
    { id: "invoice-generator", label: "Invoice Generator", icon: <MdReceiptLong /> },
    // { id: "quote-generator", label: "Quote Generator", icon: <MdAssignmentTurnedIn /> },
    { id: "legacy-certificates", label: "Legacy Certificates", icon: <MdCloudUpload /> },
    { id: "analytics", label: "Google Analytics", icon: <MdAnalytics /> },
    { id: "sms-logs", label: "SMS Logs", icon: <MdSms /> },
    { id: "docs", label: "Docs", icon: <MdMenuBook /> },
    { id: "settings", label: "Settings", icon: <MdSettings /> },
  ];

  return (
    <div style={{ display: "flex", flexDirection: "column" }}>
      <div className="admin-header-spacer" />

      {/* Mobile scrollable tab bar - hidden on desktop via media query */}
      <div className="admin-mobile-tabs-wrapper">
        <div className="admin-mobile-tabs" style={{
          display: "flex",
          overflowX: "auto",
          backgroundColor: "#f8f9fa",
          borderBottom: "1px solid #e0e0e0",
          WebkitOverflowScrolling: "touch" as any,
          scrollbarWidth: "none" as any,
          msOverflowStyle: "none" as any,
          paddingRight: "40px",
        }}>
          {tabs.map((tab) => (
            <button
              key={tab.id}
              onClick={() => setActiveTab(tab.id)}
              style={{
                flex: "0 0 auto",
                padding: "12px 10px",
                border: "none",
                borderBottom: activeTab === tab.id ? "3px solid #00adb5" : "3px solid transparent",
                background: "transparent",
                color: activeTab === tab.id ? "#00adb5" : "#555",
                cursor: "pointer",
                fontSize: "0.85em",
                fontWeight: activeTab === tab.id ? "700" : "500",
                whiteSpace: "nowrap",
                transition: "all 0.2s",
                display: "flex",
                alignItems: "center",
                gap: "6px",
              }}
            >
              <span style={{ display: "flex", alignItems: "center", fontSize: "1.1em" }}>{tab.icon}</span>
              {tab.label}
            </button>
          ))}
        </div>
        <div className="admin-mobile-tabs-fade" />
      </div>

      <div style={{ display: "flex", gap: "0", minHeight: "calc(100vh - 90px)" }}>
        {/* Left Sidebar - Desktop only */}
        <div
          className="admin-desktop-sidebar"
          style={{
            width: "280px",
            backgroundColor: "#f8f9fa",
            borderRight: "1px solid #e0e0e0",
            display: "flex",
            flexDirection: "column",
            height: "fit-content",
            position: "sticky",
            top: "85px",
          }}
        >
          <div style={{ padding: "20px 0" }}>
            <h3 style={{ padding: "0 20px 20px 20px", margin: "0", fontSize: "1.1em", color: "#333", fontWeight: "600" }}>
              Admin Menu
            </h3>
            <nav style={{ display: "flex", flexDirection: "column" }}>
              {tabs.map((tab) => (
                <button
                  key={tab.id}
                  onClick={() => setActiveTab(tab.id)}
                  style={{
                    padding: "14px 20px",
                    border: "none",
                    background: activeTab === tab.id ? "#00adb5" : "transparent",
                    color: activeTab === tab.id ? "white" : "#333",
                    textAlign: "left",
                    cursor: "pointer",
                    fontSize: "0.95em",
                    fontWeight: activeTab === tab.id ? "600" : "500",
                    transition: "all 0.2s",
                    borderLeft: activeTab === tab.id ? "4px solid #009ba3" : "4px solid transparent",
                    display: "flex",
                    alignItems: "center",
                    gap: "10px",
                  }}
                  onMouseEnter={(e) => {
                    if (activeTab !== tab.id) {
                      e.currentTarget.style.background = "#f0f0f0";
                    }
                  }}
                  onMouseLeave={(e) => {
                    if (activeTab !== tab.id) {
                      e.currentTarget.style.background = "transparent";
                    }
                  }}
                >
                  <span style={{ display: "flex", alignItems: "center", fontSize: "1.2em" }}>{tab.icon}</span>
                  {tab.label}
                </button>
              ))}
            </nav>
          </div>

          {/* Sign Out Button - Always visible */}
          <div style={{ padding: "20px", borderTop: "1px solid #e0e0e0", backgroundColor: "#fff", marginTop: "auto" }}>
            <SignOutButton />
          </div>
        </div>

        {/* Main Content Area - Scrollable */}
        <div
          style={{
            flex: 1,
            padding: "40px 40px 40px 20px",
            overflowY: "auto",
            backgroundColor: "#fff",
          }}
        >
          <h1 style={{ marginTop: "0", marginBottom: "5px", color: "#333" }}>
            Hi, {user?.firstName} 👋🏼
          </h1>
          <h2 style={{ margin: "0 0 25px 0", color: "#666", fontSize: "1.3em", fontWeight: "500" }}>
            Welcome to your Admin Portal
          </h2>

          {activeTab === "pending-eicr" && <PendingEICRApprovals user={user} />}
          {activeTab === "bookings" && <AdminBookings user={user} role={role} />}
          {activeTab === "eicr-admin" && <EICRForm />}
          {activeTab === "myconstructor" && <MyConstructorEICR />}
          {activeTab === "invoice-generator" && <InvoiceGenerator />}
          {/* {activeTab === "quote-generator" && <QuoteGenerator />} */}
          {activeTab === "legacy-certificates" && <LegacyCertificatesUpload />}
          {activeTab === "analytics" && <GoogleAnalyticsDashboard />}
          {activeTab === "sms-logs" && <SMSLogs user={user} />}
          {activeTab === "docs" && <AdminDocs />}
          {activeTab === "settings" && <Admin user={user} />}
        </div>
      </div>
    </div>
  );
}
